import Axios, { AxiosError, AxiosRequestConfig } from 'axios';
import {
    clearAuthStorage,
    getRefreshToken,
    setAccessToken,
} from '../../common/helpers';

const { REACT_APP_BASE_URL } = process.env;
const SC_URL = `${REACT_APP_BASE_URL?.replace('/avatar-core/', '')}/sc/api/auth`;
const REFRESH_URL = `${SC_URL}/refreshToken`;
const LOGIN_URL = SC_URL;

type RetryableConfig = AxiosRequestConfig & { _retry?: boolean };

let isRefreshing = false;
let pendingQueue: Array<{
    resolve: (token: string) => void;
    reject: (err: unknown) => void;
}> = [];

const flushQueue = (error: unknown, token: string | null) => {
    pendingQueue.forEach(({ resolve, reject }) => {
        if (token) resolve(token);
        else reject(error);
    });
    pendingQueue = [];
};

const forceLogout = () => {
    clearAuthStorage();
    window.location.assign('/login');
};

const isAuthEndpoint = (url: string | undefined): boolean => {
    if (!url) return false;
    return url.startsWith(REFRESH_URL) || url === LOGIN_URL;
};

export function AxiosResponseInterceptor() {}

export function AxiosResponseErrorInterceptor(error: AxiosError) {
    const originalRequest = error.config as RetryableConfig | undefined;

    if (!error.response || error.response.status !== 401 || !originalRequest) {
        return Promise.reject(error);
    }

    if (isAuthEndpoint(originalRequest.url)) {
        return Promise.reject(error);
    }

    if (originalRequest._retry) {
        forceLogout();
        return Promise.reject(error);
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
        forceLogout();
        return Promise.reject(error);
    }

    if (isRefreshing) {
        return new Promise((resolve, reject) => {
            pendingQueue.push({
                resolve: () => {
                    originalRequest._retry = true;
                    resolve(Axios(originalRequest));
                },
                reject,
            });
        });
    }

    isRefreshing = true;
    return new Promise((resolve, reject) => {
        Axios.get(REFRESH_URL, {
            params: { refreshToken },
            headers: { skipAuthHeaders: 'true' },
        })
            .then((response) => {
                const newAccessToken: string | undefined = response?.data?.content?.accessToken;
                if (!newAccessToken) {
                    throw new Error('Refresh response missing accessToken');
                }
                setAccessToken(newAccessToken);
                flushQueue(null, newAccessToken);
                originalRequest._retry = true;
                resolve(Axios(originalRequest));
            })
            .catch((refreshError) => {
                flushQueue(refreshError, null);
                forceLogout();
                reject(refreshError);
            })
            .finally(() => {
                isRefreshing = false;
            });
    });
}
